/** Badges statuts/priorités : mapping exact des constantes *_META du design Chronos Enrichissement. */

import type { TimesheetStatus } from "../api/types";

export interface ChipMeta {
  label: string;
  bg: string;
  color: string;
}

const GRAY = { bg: "var(--chip-gray-bg)", color: "var(--chip-gray-fg)" };
const MUTED = { bg: "var(--chip-gray-bg)", color: "var(--fg3)" };
const BLUE = { bg: "var(--brand-soft)", color: "var(--brand)" };
const BLUE_ACTIVE = { bg: "var(--brand-soft-2)", color: "var(--brand-600)" };
const GREEN = { bg: "var(--success-soft)", color: "var(--success-fg)" };
const RED = { bg: "var(--danger-soft)", color: "var(--danger-fg)" };
const AMBER = { bg: "var(--warning-soft)", color: "var(--warning-fg)" };
const TEAL = { bg: "var(--teal-soft)", color: "var(--teal)" };

export const LTE_STATUS_META: Record<string, ChipMeta> = {
  planned: { label: "Planifiée", ...GRAY },
  assigned: { label: "Affectée", ...BLUE },
  accepted: { label: "Acceptée", ...BLUE },
  started: { label: "En cours", ...BLUE_ACTIVE },
  paused: { label: "En pause", ...AMBER },
  blocked: { label: "Bloquée", ...RED },
  completed: { label: "Terminée", ...GREEN },
  cancelled: { label: "Annulée", ...MUTED },
  rejected: { label: "Refusée", ...MUTED },
};

export const TM_STATUS_META: Record<string, ChipMeta> = {
  todo: { label: "À faire", ...GRAY },
  in_progress: { label: "En cours", ...BLUE_ACTIVE },
  blocked: { label: "Bloquée", ...RED },
  done: { label: "Terminée", ...GREEN },
  cancelled: { label: "Annulée", ...MUTED },
  archived: { label: "Archivée", ...MUTED },
};

export const TM_ORIGIN_META: Record<
  string,
  ChipMeta & { dot: string; locked: boolean }
> = {
  native: {
    label: "Personnelle",
    ...GRAY,
    dot: "var(--fg4)",
    locked: false,
  },
  terrain: { label: "Terrain", ...BLUE, dot: "var(--brand)", locked: true },
  temps: { label: "Temps", ...TEAL, dot: "var(--teal)", locked: true },
};

/** Sessions temps TAWA (lecture seule dans « Mes tâches »). */
export const TEMPS_STATUS_META: Record<string, ChipMeta> = {
  active: { label: "En cours", ...TEAL },
  closed: { label: "Clôturé", ...GRAY },
};

/** Rôle opérationnel RACI (task_assignments.role) : tâches natives TM
 * uniquement, badge violet du design. */
const PURPLE = { bg: "var(--purple-soft)", color: "var(--purple)" };
export const TM_ROLE_META: Record<string, ChipMeta> = {
  driver: { label: "Pilote", ...PURPLE },
  contributor: { label: "Contributeur", ...PURPLE },
  approver: { label: "Approbateur", ...PURPLE },
  informed: { label: "Informé", ...PURPLE },
  consulted: { label: "Consulté", ...PURPLE },
};

/** Priorités LTE + TM : clés des domaines Go (LTE: critical, TM: urgent), libellés design. */
export const LTE_PRIORITY_META: Record<string, ChipMeta> = {
  critical: { label: "Critique", ...RED },
  urgent: { label: "Urgente", ...RED },
  high: { label: "Haute", ...AMBER },
  medium: { label: "Normale", ...GRAY },
  low: { label: "Basse", ...MUTED },
};

/** Types d'exécution LTE (taxonomie fermée du domaine Go). */
export const LTE_TYPE_LABEL: Record<string, string> = {
  standard: "Standard",
  emergency: "Urgence",
  maintenance: "Maintenance",
  inspection: "Inspection",
  installation: "Installation",
};

export const WBS_STATUS_META: Record<string, ChipMeta> = {
  todo: { label: "À faire", ...GRAY },
  in_progress: { label: "En cours", ...BLUE_ACTIVE },
  blocked: { label: "Bloqué", ...RED },
  done: { label: "Terminé", ...GREEN },
  cancelled: { label: "Annulé", ...MUTED },
};

export const MILESTONE_META: Record<string, ChipMeta> = {
  done: { label: "Fait", ...GREEN },
  late: { label: "En retard", ...RED },
  upcoming: { label: "À venir", ...GRAY },
};

export const PROJECT_STATUS_META: Record<string, ChipMeta> = {
  active: { label: "Actif", ...BLUE },
  on_hold: { label: "En pause", ...AMBER },
  completed: { label: "Terminé", ...GREEN },
  cancelled: { label: "Annulé", ...MUTED },
};

/** Statut de la feuille d'une semaine (vue équipe), en classes de tag. */
export const TIMESHEET_STATUS_TAG: Record<
  TimesheetStatus,
  { label: string; cls: string }
> = {
  draft: { label: "Brouillon", cls: "tag--brand" },
  submitted: { label: "Soumise", cls: "tag--neutral" },
  approved: { label: "Approuvée", cls: "tag--neutral" },
  rejected: { label: "Rejetée", cls: "tag--neutral" },
  corrected: { label: "Corrigée", cls: "tag--neutral" },
  locked: { label: "Verrouillée", cls: "tag--neutral" },
};

/** Ce que le statut demande au salarié, dit sous le statut. */
export const TIMESHEET_STATUS_HINT: Record<TimesheetStatus, string> = {
  draft: "Relisez-la, puis soumettez-la en fin de période",
  submitted: "Envoyée, en attente de validation par le manager",
  approved: "Approuvée par le manager",
  rejected: "Refusée par le manager : corrigez-la, puis soumettez-la",
  corrected: "Corrigée, à soumettre de nouveau",
  locked: "Validée et verrouillée, partie dans l'export paie",
};

/** Sans feuille générée, la semaine est encore en cours. */
export function timesheetTag(status: TimesheetStatus | undefined): {
  label: string;
  cls: string;
} {
  return status
    ? TIMESHEET_STATUS_TAG[status]
    : { label: "En cours", cls: "tag--brand" };
}

export function chipMeta(
  map: Record<string, ChipMeta>,
  key: string | undefined,
): ChipMeta {
  return (key && map[key]) || { label: key || "-", ...GRAY };
}
